// eslint-disable-next-line no-restricted-imports
import { type ZodOptional, z, ZodArray, ZodType } from 'zod'

declare module 'zod' {
  interface ZodType<Output, Input, Internals> {
    nullishCoalescingUndefined(): ZodOptional<
      ZodType<
        Exclude<Output, null> | undefined,
        Input | null | undefined,
        // @ts-expect-error // TODO: Fix internals Output mismatch
        Internals
      >
    >
  }

  interface ZodArray<T extends z.core.SomeType = z.core.$ZodType> {
    safeFilteredArray(options?: {
      onError?: (error: z.ZodError, item: unknown) => void
    }): ZodType<z.infer<T>[], unknown[]>
  }
}

/**
 * Adds a method to ZodType that allows for nullish coalescing to undefined.
 * Method preprocesses the input value, replacing `null` with `undefined`.
 * It is useful when you want to ensure that `null` values are treated
 * as `undefined` in your schema.
 *
 * @example
 * const schema = z.string().nullishCoalescingUndefined();
 * schema.parse(null); // returns undefined
 * schema.parse(undefined); // returns undefined
 * schema.parse("hello"); // returns "hello"
 * schema.parse(123); // throws ZodError
 */
ZodType.prototype.nullishCoalescingUndefined = function (this) {
  return z.pipe(
    z.transform((val: unknown) => val ?? undefined),
    this.optional()
  )
}

/**
 * Adds a method to ZodType that creates a safe array parser.
 * Method keeps only array items that are valid for the item schema.
 *
 * @example
 * const schema = z.array(z.object({ id: z.number() })).safeFilteredArray();
 * schema.parse([{ id: 1 }, { id: 'x' }]); // returns [{ id: 1 }]
 *
 * @example
 * const schema = z.array(z.object({ id: z.number() })).safeFilteredArray({
 *   onError: (error, item) => console.warn('Invalid item', item, error.issues),
 * });
 * schema.parse([{ id: 1 }, { id: 'x' }]); // returns [{ id: 1 }], logs warning for { id: 'x' }
 */
ZodArray.prototype.safeFilteredArray = function <T extends z.core.SomeType>(
  this: ZodArray<T>,
  options?: { onError?: (error: z.ZodError, item: unknown) => void }
) {
  const elementSchema = this.element as unknown as ZodType<z.infer<T>>

  return z.array(z.unknown()).transform<z.infer<T>[]>((items: unknown[]) => {
    return items.reduce<z.infer<T>[]>((result, item) => {
      const parsedItem = elementSchema.safeParse(item)
      if (parsedItem.success) {
        result.push(parsedItem.data)
      } else {
        options?.onError?.(parsedItem.error, item)
      }

      return result
    }, [])
  })
}

export { z }
